/**
 * The view of "My office network" ↔ the URL (CU-869fmjzrh D14, CA 4). Relative pick-ups and relative captures travel as
 * keys, so a copied link opened another day moves with them; fixed dates travel with their dates. The collapsed groups
 * and the hidden rankings are remembered per user but are not part of the link. Pure and alias-free.
 */
import { fromIso, toIso } from '../../../@services/dates.js'
import { isRangeKey, isValidFixed } from '../../../@services/ranges.js'
import { CARS, NO_CARS, parseCars } from '../../../@services/cars.js'
import { flag, idList, positiveInt } from '../../../@services/params.js'
import { CAPTURES, MAX_CAPTURE_DAYS, isValidCapture } from './capture.js'

export const VIEWS = Object.freeze(['length', 'pickup', 'provider'])
export const SHOW_BY = Object.freeze(['day', 'week', 'month'])
export const ME_AS = Object.freeze(['brand', 'site'])
export const COMPARE = Object.freeze(['day', 'week', 'year', 'yearSame'])
export const STATS = Object.freeze(['min', 'avg', 'max'])
export const PRICES = Object.freeze(['total', 'day'])
export const GROUP_BY = Object.freeze(['country', 'type', 'none'])
export const OFFICE_TYPES = Object.freeze(['airport', 'downtown', 'train_station', 'truck', 'other'])
export const DEFAULT_LENGTHS = Object.freeze([1, 3, 7, 14])
export const MAX_LENGTHS = 8

// CA 4: the whole network by country, the lengths with prices, next 4 weeks, all websites, total, my brand, yesterday
// against the day before, Min, by rental length
export const DEFAULT_VIEW = Object.freeze({
	v: 'length', by: 'day', me: 'brand',
	countries: [], types: [], offices: [], officeGroup: null,
	lens: null, lor: 7, sites: [], siteGroup: null,
	cars: CARS.ALL, carsIds: [], tier: null, providers: [],
	range: 'next4w', from: null, to: null, cap: 'yday', cfrom: null, cto: null,
	cmp: 'day', stat: 'min', price: 'total', grp: 'country', chg: false, sort: 'name', dir: 'asc',
})

const codes = v => {
	const xs = String(v || '').split(',').map(x => x.trim().toUpperCase()).filter(Boolean)
	return xs.length && xs.every(x => /^[A-Z]{2,3}$/.test(x)) ? [...new Set(xs)] : null
}
const types = v => {
	const xs = String(v || '').split(',').filter(Boolean)
	return xs.length && xs.every(x => OFFICE_TYPES.includes(x)) ? [...new Set(xs)] : null
}
const sortKey = v => (v === 'name' || /^(col|change):\d{1,3}$/.test(String(v || '')) ? v : null)

/** Only the valid keys of a URL (or a stored view). A whole view (it has `range`) says its filters even when empty. */
export function parseView(params) {
	const get = k => (typeof params?.get === 'function' ? params.get(k) : params?.[k])
	const out = {}
	const one = (k, list, key = k) => { if (list.includes(get(k))) out[key] = get(k) }
	one('v', VIEWS); one('by', SHOW_BY); one('me', ME_AS); one('cmp', COMPARE); one('stat', STATS); one('price', PRICES); one('grp', GROUP_BY)
	const whole = !!get('range')

	const c = codes(get('cty')); if (c) out.countries = c; else if (whole) out.countries = []
	const t = types(get('typ')); if (t) out.types = t; else if (whole) out.types = []
	const og = positiveInt(get('og'))
	const ofc = idList(get('ofc'), 2000)
	if (og) Object.assign(out, { officeGroup: og, offices: [] })
	else if (ofc) Object.assign(out, { officeGroup: null, offices: ofc })
	else if (whole) Object.assign(out, { officeGroup: null, offices: [] })

	const lens = idList(get('lens'), MAX_LENGTHS)
	if (lens && lens.every(x => x < 60)) out.lens = [...lens].sort((a, b) => a - b)
	else if (whole) out.lens = null
	const lor = positiveInt(get('lor'))
	if (lor && lor < 60) out.lor = lor

	const sg = positiveInt(get('sg'))
	const sites = idList(get('sites'), 2000)
	if (sg) Object.assign(out, { siteGroup: sg, sites: [] })
	else if (sites) Object.assign(out, { siteGroup: null, sites })
	else if (whole) Object.assign(out, { siteGroup: null, sites: [] })

	if (get('cars') || whole) Object.assign(out, get('cars') ? parseCars(get('cars'), get('carsIds')) : { ...NO_CARS, carsIds: [] })
	const tier = positiveInt(get('tier'))
	const providers = idList(get('providers'))
	if (tier) Object.assign(out, { tier, providers: [] })
	else if (providers) Object.assign(out, { tier: null, providers })
	else if (whole) Object.assign(out, { tier: null, providers: [] })

	const range = get('range')
	if (range === 'fixed') {
		const from = fromIso(get('from')), to = fromIso(get('to'))
		if (isValidFixed({ from, to })) Object.assign(out, { range, from: toIso(from), to: toIso(to) })
	} else if (isRangeKey(range) && range !== 'fixed') {
		out.range = range
	}
	const cap = get('cap')
	if (cap === 'fixed') {
		const from = fromIso(get('cfrom')), to = fromIso(get('cto'))
		if (isValidCapture({ from, to })) Object.assign(out, { cap, cfrom: toIso(from), cto: toIso(to) })
	} else if (Object.prototype.hasOwnProperty.call(CAPTURES, cap) && cap !== 'fixed') {
		out.cap = cap
	}

	const chg = flag(get('chg')); if (chg !== null) out.chg = chg
	const sort = sortKey(get('sort')); if (sort) out.sort = sort
	if (['asc', 'desc'].includes(get('dir'))) out.dir = get('dir')
	return out
}

/** The view: first what the URL says, then what was stored, then the defaults */
export function resolveView(...sources) {
	const view = { ...DEFAULT_VIEW }
	for (const source of [...sources].reverse()) Object.assign(view, parseView(source || {}))
	if (view.range !== 'fixed') Object.assign(view, { from: null, to: null })
	if (view.cap !== 'fixed') Object.assign(view, { cfrom: null, cto: null })
	return view
}

/** View → query string. Relative ranges and captures never carry dates. */
export function viewToParams(view) {
	const p = new URLSearchParams()
	p.set('v', view.v)
	if (view.v === 'pickup') p.set('by', view.by)
	p.set('me', view.me)
	if (view.countries?.length) p.set('cty', view.countries.join(','))
	if (view.types?.length) p.set('typ', view.types.join(','))
	if (view.officeGroup) p.set('og', String(view.officeGroup))
	else if (view.offices?.length) p.set('ofc', view.offices.join(','))
	if (view.lens?.length) p.set('lens', view.lens.join(','))
	p.set('lor', String(view.lor))
	if (view.siteGroup) p.set('sg', String(view.siteGroup))
	else if (view.sites?.length) p.set('sites', view.sites.join(','))
	if (view.cars && view.cars !== CARS.ALL && view.carsIds?.length) {
		p.set('cars', view.cars)
		p.set('carsIds', view.carsIds.join(','))
	}
	if (view.tier) p.set('tier', String(view.tier))
	else if (view.providers?.length) p.set('providers', view.providers.join(','))
	p.set('range', view.range)
	if (view.range === 'fixed' && view.from && view.to) { p.set('from', view.from); p.set('to', view.to) }
	p.set('cap', view.cap)
	if (view.cap === 'fixed' && view.cfrom && view.cto) { p.set('cfrom', view.cfrom); p.set('cto', view.cto) }
	p.set('cmp', view.cmp)
	p.set('stat', view.stat)
	p.set('price', view.price)
	p.set('grp', view.grp)
	p.set('chg', view.chg ? '1' : '0')
	p.set('sort', view.sort)
	p.set('dir', view.dir)
	return p.toString()
}

export const viewToStored = view => Object.fromEntries(new URLSearchParams(viewToParams(view)))
export const storageKey = uid => `rentacar.dashboards.officeNetwork.${uid || 'anon'}`
export const uiStorageKey = uid => `rentacar.dashboards.officeNetwork.ui.${uid || 'anon'}`
export { MAX_CAPTURE_DAYS }

/**
 * The lengths of the lengths view (CA 4, D6): the user's choice; by default 1, 3, 7 and 14 among the lengths BI offers,
 * or the first four when fewer than two of those are there.
 */
export function shownLengths(view, options = []) {
	const offered = options.map(o => Number(o.value ?? o)).filter(n => Number.isInteger(n) && n > 0)
	if (view.lens?.length) return [...view.lens]
	const defaults = DEFAULT_LENGTHS.filter(l => !offered.length || offered.includes(l))
	if (defaults.length >= 2 || !offered.length) return defaults
	return [...offered].sort((a, b) => a - b).slice(0, 4)
}
